import { RouteProp, useFocusEffect, useNavigation, useRoute } from "@react-navigation/native";
import { useCallback, useEffect, useMemo, useState } from "react";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { useAuth } from "../hooks/useAuth";
import {
  useGetAiTutorQuery,
  useGetAiTutorVideosQuery,
  useGetCompanionChatByAiTutorIdQuery,
} from "@axio/graphql/generated/graphql";
import AITutorChatLayoutContainer from "../containers/chat/AITutorChatLayoutContainer/AITutorChatLayoutContainer";
import AITutorChatLayoutContainerV2 from "../containers/chat/AITutorChatLayoutContainerV2/AITutorChatLayoutContainerV2";
import LTILayout from "../layouts/LTILayout";
import Loader from "../components/common/status/Loader/Loader";
import type { RootStackParamList } from "../navigation/navigation.types";
import { useAppConfig } from "../AppConfigProvider";
import Sentry from "../utils/sentry";
import type { Objective } from "../containers/companion/LearningGoalsContainer";
import type { ExplorationMediaItem } from "../components/learning-activity/ExplorationMenu/ExplorationMenu";

const noop = () => {};

function LTIILEView() {
  const route = useRoute<RouteProp<RootStackParamList, "LTIILE">>();
  const { activityId, accessToken, refreshToken } = route.params ?? {};
  const { login, isAuthenticated, didCheckToken } = useAuth();
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const { useILEGraphV2 } = useAppConfig() ?? {};

  const [authReady, setAuthReady] = useState(false);
  const [completedObjectiveIds, setCompletedObjectiveIds] = useState<string[]>([]);

  // --- Two-path auth flow ---
  //
  // Path 1 (direct LTI launch): The backend redirects here with accessToken and
  // refreshToken as URL params. useFocusEffect calls login() to bootstrap the
  // auth context. Note: login() sets isAuthenticated=true which makes isAuthReady
  // false in AppNavigator while me/permissions hydrate, temporarily unmounting the
  // navigator. When the navigator remounts, this view re-mounts via the linking
  // config matching /lti/ile/:activityId — but route.params no longer contain the
  // consumed tokens, so Path 2 takes over.
  //
  // Path 2 (pre-authed arrival): When the user arrives without URL tokens — either
  // via LoginLTIILEView's navigation.reset (A2) or after the navigator remounts
  // post-login — this useEffect checks the existing auth state and sets authReady.
  useFocusEffect(
    useCallback(() => {
      if (!accessToken || !refreshToken) return;

      let isActive = true;

      const bootstrap = async () => {
        try {
          await login(accessToken, refreshToken);
        } catch (error) {
          Sentry.captureException(error);
          if (!isActive) return;
          navigation.reset({
            index: 0,
            routes: [{ name: "LTIErrorView", params: { error: "auth_failed" } }],
          });
          return;
        }
        if (!isActive) return;
        setAuthReady(true);
      };

      void bootstrap();

      return () => {
        isActive = false;
      };
    }, [accessToken, login, navigation, refreshToken])
  );

  useEffect(() => {
    if (accessToken && refreshToken) return;
    if (!didCheckToken) return;
    if (isAuthenticated) {
      setAuthReady(true);
    } else {
      navigation.reset({
        index: 0,
        routes: [{ name: "LTIErrorView", params: { error: "auth_failed" } }],
      });
    }
  }, [accessToken, didCheckToken, isAuthenticated, navigation, refreshToken]);

  const {
    data: aiTutorData,
    loading: aiTutorLoading,
    error: aiTutorError,
    refetch: refetchAiTutor,
  } = useGetAiTutorQuery({
    variables: { id: activityId ?? "" },
    skip: !authReady || !activityId,
  });

  const { data: companionChatByAiTutorData, loading: companionChatByAiTutorLoading } =
    useGetCompanionChatByAiTutorIdQuery({
      variables: { aiTutorId: activityId ?? "" },
      skip: !authReady || !activityId,
      fetchPolicy: "network-only",
    });

  const { data: aiTutorVideosData, refetch: refetchAiTutorVideos } = useGetAiTutorVideosQuery({
    variables: { id: activityId ?? "" },
    skip: !authReady || !activityId,
    fetchPolicy: "network-only",
  });

  // Redirect to error if activityId is missing after auth resolves
  useEffect(() => {
    if (!authReady || activityId) return;
    navigation.reset({
      index: 0,
      routes: [{ name: "LTIErrorView", params: { error: "invalid_launch" } }],
    });
  }, [activityId, authReady, navigation]);

  // Navigate to error if activity not found after loading completes
  useEffect(() => {
    if (!authReady || aiTutorLoading || aiTutorData === undefined) return;
    if (aiTutorError || !aiTutorData?.getAITutor?.data?.[0]) {
      navigation.reset({
        index: 0,
        routes: [{ name: "LTIErrorView", params: { error: "missing_activity" } }],
      });
    }
  }, [aiTutorData, aiTutorError, aiTutorLoading, authReady, navigation]);

  const explorationItems = useMemo<ExplorationMediaItem[]>(() => {
    const videos = (aiTutorVideosData?.getAITutorVideos?.data ?? []).filter(
      (video): video is NonNullable<typeof video> => video != null
    );
    return videos.map((video) => ({
      id: video.id ?? video.videoId ?? "",
      title: video.title ?? "",
      type: "video" as const,
      metadata: "Suggested",
      thumbnail:
        video.thumbnailUrl || (video.videoId ? `https://img.youtube.com/vi/${video.videoId}/hqdefault.jpg` : ""),
      videoId: video.videoId,
      description: video.description ?? undefined,
      mediaUrl: video.url ?? undefined,
    }));
  }, [aiTutorVideosData]);

  const objectiveData = useMemo<Objective[]>(() => {
    const objectives = [...(aiTutorData?.getAITutor?.data?.[0]?.objectives ?? [])].sort(
      (a, b) => (a?.sequence ?? 0) - (b?.sequence ?? 0)
    );
    return objectives.reduce<Objective[]>((acc, objective) => {
      if (!objective) return acc;
      const isPersistedCompleted = objective.status?.trim().toLowerCase() === "completed";
      acc.push({
        id: objective.id,
        objective: objective.objective ?? "",
        evalCriteria: objective.evalCriteria ?? [],
        isCompleted: isPersistedCompleted || completedObjectiveIds.includes(objective.id ?? ""),
      });
      return acc;
    }, []);
  }, [aiTutorData, completedObjectiveIds]);

  useEffect(() => {
    setCompletedObjectiveIds([]);
  }, [activityId]);

  useEffect(() => {
    if (!activityId || !authReady) return;
    void refetchAiTutorVideos();
  }, [activityId, authReady, refetchAiTutorVideos]);

  const handleObjectiveCompleted = useCallback(
    (objectiveIds: string[] = []) => {
      if (objectiveIds.length > 0) {
        setCompletedObjectiveIds((previousIds) =>
          Array.from(new Set([...previousIds, ...objectiveIds.filter((id): id is string => Boolean(id))]))
        );
      }
      if (!activityId) return;
      void Promise.all([refetchAiTutor({ id: activityId }), refetchAiTutorVideos({ id: activityId })]);
    },
    [activityId, refetchAiTutor, refetchAiTutorVideos]
  );

  if (!authReady) {
    return (
      <LTILayout>
        <Loader />
      </LTILayout>
    );
  }

  if (useILEGraphV2) {
    return (
      <LTILayout>
        <AITutorChatLayoutContainerV2
          resourceId={activityId ?? ""}
          title={aiTutorData?.getAITutor?.data?.[0]?.title ?? "Learning Activity"}
          loading={aiTutorLoading}
          backButtonText="Back to module"
          onBackPress={noop}
          onModalBackPress={noop}
          hasNextActivity={false}
          hideBackButton
          hideDisclaimer
          chatData={companionChatByAiTutorData?.getCompanionChatByAiTutorId?.data ?? undefined}
          chatLoading={companionChatByAiTutorLoading}
          objectives={objectiveData}
          onObjectiveCompleted={handleObjectiveCompleted}
          explorationItems={explorationItems}
        />
      </LTILayout>
    );
  }

  return (
    <LTILayout>
      <AITutorChatLayoutContainer
        resourceId={activityId ?? ""}
        title={aiTutorData?.getAITutor?.data?.[0]?.title ?? "Learning Activity"}
        loading={aiTutorLoading}
        backButtonText="Back to module"
        onBackPress={noop}
        onModalBackPress={noop}
        hasNextActivity={false}
        hideBackButton
        hideDisclaimer
        chatData={companionChatByAiTutorData?.getCompanionChatByAiTutorId?.data}
        chatLoading={companionChatByAiTutorLoading}
        objectives={objectiveData}
        onObjectiveCompleted={handleObjectiveCompleted}
        explorationItems={explorationItems}
      />
    </LTILayout>
  );
}

export default LTIILEView;
